import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClientConsumption } from "@/components/ClientConsumption";
import { datePresets, todayLocalISO } from "@/lib/dates";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend,
} from "recharts";
import {
  Package, TrendingUp, TrendingDown, ShieldAlert, ShieldQuestion, ShieldCheck,
  MessageCircle, Pin, Store as StoreIcon, Layers, Gauge,
} from "lucide-react";
import { formatNum } from "@/lib/format";

const RED = "#E11D2A";
const DARK = "#0A0A0A";

function ClientNotesFeed() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const load = async () => {
      try { const { data } = await api.get("/client-notes"); setNotes(data); }
      catch { setNotes([]); } finally { setLoading(false); }
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  return (
    <Card className="p-5 rounded-xl border-gray-200 bg-white" data-testid="client-notes-feed">
      <div className="flex items-center gap-2 mb-3">
        <MessageCircle className="h-4 w-4 text-brand-red" />
        <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Updates from BBIPL</div>
      </div>
      {loading ? <div className="text-sm text-gray-400 py-4 text-center">Loading…</div>
        : notes.length === 0 ? <div className="text-sm text-gray-400 py-6 text-center">No updates yet.</div>
        : <div className="space-y-2 max-h-64 overflow-y-auto">
            {notes.map((n) => (
              <div key={n.id} className={`p-3 rounded-lg border ${n.pinned ? "border-red-200 bg-red-50/40" : "border-gray-100 bg-gray-50"}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  {n.pinned && <Badge className="bg-red-100 text-brand-red border border-red-200 h-4 text-[9px] px-1.5"><Pin className="h-2.5 w-2.5 mr-0.5" />Pinned</Badge>}
                  <span className="text-[10px] text-gray-500">
                    {new Date(n.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <div className="text-xs text-gray-900 whitespace-pre-wrap break-words leading-snug">{n.text}</div>
                <div className="text-[10px] text-gray-400 mt-1.5">— {n.created_by_name}</div>
              </div>
            ))}
          </div>}
    </Card>
  );
}

function KpiCard({ label, value, sub, icon: Icon, testId, color = "text-gray-900" }) {
  return (
    <Card className="p-4 rounded-xl border-gray-200 bg-white" data-testid={testId}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-brand-red" />}
        <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">{label}</div>
      </div>
      <div className={`font-display text-2xl font-black mt-1 ${color}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-1 leading-tight whitespace-pre-line">{sub}</div>}
    </Card>
  );
}

// Date-range presets — mirror /client/reports so the client gets the same mental model on both pages.
// All values are plain YYYY-MM-DD strings, no UTC drift (see /lib/dates.js).

export default function ClientDashboard() {
  const pre = useMemo(datePresets, []);
  const [preset, setPreset] = useState("thisMonth");
  const [fromDate, setFromDate] = useState(pre.thisMonth.from);
  const [toDate, setToDate] = useState(pre.thisMonth.to);
  const [storeFilterId, setStoreFilterId] = useState("");
  const [allStores, setAllStores] = useState([]);
  const [summary, setSummary] = useState(null);
  const [stockHealth, setStockHealth] = useState(null);
  const [period, setPeriod] = useState("weekly");
  const [trend, setTrend] = useState(null);
  const [dailyRows, setDailyRows] = useState([]);
  const [consumptionMTD, setConsumptionMTD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendView, setTrendView] = useState("store");

  useEffect(() => {
    // Store list for the filter dropdown
    api.get("/stores").then(r => setAllStores(r.data)).catch(() => setAllStores([]));
    // Consumption MTD — fixed 1st of month → today
    api.get(`/client/consumption?from_date=${pre.thisMonth.from}&to_date=${pre.thisMonth.to}`)
       .then(r => setConsumptionMTD(r.data)).catch(() => setConsumptionMTD(null));
  }, [pre.thisMonth.from, pre.thisMonth.to]);

  const applyPreset = (name) => {
    setPreset(name);
    if (name === "custom") return;
    const p = pre[name]; if (!p) return;
    setFromDate(p.from); setToDate(p.to);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h, t, r] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/client/stock-health"),
        api.get(`/client/sales-trend?period=${period}`),
        api.get("/dashboard/stores"),
      ]);
      setSummary(s.data); setStockHealth(h.data); setTrend(t.data); setDailyRows(r.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [period]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const overallStockColor = useMemo(() => {
    if (!stockHealth) return "bg-gray-100 text-gray-500";
    if (stockHealth.overall === "CRITICAL") return "bg-red-100 text-brand-red border border-red-300";
    if (stockHealth.overall === "MODERATE" || stockHealth.overall === "FAIR") return "bg-amber-100 text-amber-800 border border-amber-300";
    if (stockHealth.overall === "NO DATA") return "bg-gray-100 text-gray-600 border border-gray-300";
    return "bg-green-100 text-green-800 border border-green-300";
  }, [stockHealth]);

  // Chart data — store-wise bar (top 15 stores by cups)
  const barData = useMemo(() => {
    if (!trend) return [];
    if (trendView === "zone") {
      return (trend.by_zone || []).map((z) => ({
        name: z.zone,
        cups: z.cups,
      }));
    }
    return (trend.all || []).slice(0, 15).map((r) => ({
      name: r.store_name.length > 14 ? r.store_name.slice(0, 12) + "…" : r.store_name,
      cups: r.cups,
    }));
  }, [trend, trendView]);

  // Line data — one point per day in window. If "zone" view, one line per zone; else one aggregate line.
  const lineData = useMemo(() => {
    if (!trend) return { rows: [], series: [] };
    if (trendView === "zone") {
      const perZone = trend.per_zone_by_date || {};
      const zones = Object.keys(perZone);
      const dates = (trend.line_total || []).map((p) => p.date);
      const rows = dates.map((d) => {
        const r = { date: d.slice(5) };
        zones.forEach((z) => {
          const found = (perZone[z] || []).find((p) => p.date === d);
          r[z] = found ? found.cups : 0;
        });
        return r;
      });
      return { rows, series: zones };
    }
    const rows = (trend.line_total || []).map((p) => ({ date: p.date.slice(5), cups: p.cups }));
    return { rows, series: ["cups"] };
  }, [trend, trendView]);

  const totalStores = trend?.total_stores || 30;
  const activePeriod = trend?.active_stores_period || 0;
  const trendCups = trend?.totals?.cups || 0;
  const days = trend?.days || 7;
  const avgCupsPerDay = Math.round(trendCups / Math.max(1, days));
  const avgCupsPerStorePerDay = activePeriod > 0
    ? Math.round(trendCups / (Math.max(1, activePeriod) * Math.max(1, days)))
    : 0;

  const cumulativeCups = summary?.kpis?.total_cups_cumulative || 0;
  const cupsToday = summary?.kpis?.total_glasses || 0;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header intentionally shows a neutral title — the word "Client" is removed per COO request. */}
      <AppHeader title="Coke @9" subtitle="Coca-Cola India · Live operations" />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">
          {loading && !summary ? (
            <div className="p-8 text-center text-gray-500">Loading…</div>
          ) : (
            <>
              {/* Top KPI row — client-safe: Total Cups (cumulative), Cups Today, Stores Live, Average sale */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard label="Total cups sold" value={formatNum(cumulativeCups)}
                  sub="cumulative · all time" icon={Package}
                  testId="client-kpi-cumulative-cups" />
                <KpiCard label="Cups sold today" value={formatNum(cupsToday)}
                  sub={`live · updates every minute`}
                  icon={Gauge}
                  testId="client-kpi-cups-today" />
                <KpiCard label="Total stores live"
                  value={`${activePeriod}${totalStores ? ` / ${totalStores}` : ""}`}
                  sub={`reported sales in last ${days} day${days > 1 ? "s" : ""}`}
                  icon={StoreIcon}
                  testId="client-kpi-stores-live" />
                <KpiCard label="Average sale"
                  value={`${formatNum(avgCupsPerDay)} Cups/Day`}
                  sub={`${formatNum(avgCupsPerStorePerDay)} Cups/Store/Day`}
                  icon={TrendingUp}
                  testId="client-kpi-avg-sale" />
              </div>

              {/* Material Consumption — dashboard view: THREE clean tiles only.
                  Period is fixed to 1st of current month → today (see /lib/dates.js). No filter, no calendar, no table, no download here.
                  The calendar drill-down + exports live inside /coca-cola/reports Consumption tab. */}
              {/* moved below (rendered after Top/Low performing stores) */}

              {/* Stock health — simplified: highlight only Moderate/Critical stores */}
              <Card className="p-5 rounded-xl border-gray-200 bg-white" data-testid="client-stock-health">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Stock health across all stores</div>
                    <div className="mt-1 flex items-center gap-3">
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest ${overallStockColor}`} data-testid="client-stock-band">
                        {stockHealth?.overall === "CRITICAL" ? <ShieldAlert className="h-3.5 w-3.5" /> :
                         stockHealth?.overall === "MODERATE" ? <ShieldQuestion className="h-3.5 w-3.5" /> :
                         stockHealth?.overall === "NO DATA" ? <ShieldQuestion className="h-3.5 w-3.5" /> :
                         <ShieldCheck className="h-3.5 w-3.5" />}
                        {stockHealth?.overall || "—"}
                      </div>
                      <div className="text-xs text-gray-500" data-testid="client-stock-summary-counts">
                        {stockHealth?.open_notes > 0
                          ? <span className="text-amber-700 font-semibold">{stockHealth.open_notes} open note{stockHealth.open_notes > 1 ? "s" : ""}</span>
                          : <span className="text-green-700 font-semibold">All stores OK — no open notes</span>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Only surface the stores that actually need attention. Good stores are not listed here — they're the majority and not actionable. */}
                <div className="mt-4">
                  {(stockHealth?.action_needed || []).length === 0 ? (
                    <div className="text-center text-sm text-gray-500 bg-green-50 border border-green-200 rounded-lg p-4"
                         data-testid="client-stock-all-good">
                      {stockHealth?.no_data > 0 && (stockHealth?.good || 0) + (stockHealth?.moderate || 0) + (stockHealth?.critical || 0) === 0
                        ? <>No store has reported stock data yet — nothing to flag.</>
                        : <>Reporting stores are in the <span className="font-semibold text-green-700">Good</span> band. No action needed.</>}
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="client-stock-attention-table">
                        <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                          <tr>
                            <th className="text-left px-3 py-2">Store</th>
                            <th className="text-left px-3 py-2">Zone</th>
                            <th className="text-left px-3 py-2">Short of</th>
                            <th className="text-right px-3 py-2">Days of cover</th>
                            <th className="text-left px-3 py-2">Band</th>
                            <th className="text-left px-3 py-2">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stockHealth.action_needed.map((s) => (
                            <tr key={s.store_id} className="border-t border-gray-100" data-testid={`client-stock-row-${s.store_code || s.store_id.slice(0, 6)}`}>
                              <td className="px-3 py-2 font-semibold">{s.store_name}</td>
                              <td className="px-3 py-2 text-xs text-gray-600">{s.zone || "—"}</td>
                              <td className="px-3 py-2 text-xs">
                                <span className="font-semibold">{s.weakest_item || "—"}</span>
                                <div className="text-[10px] text-gray-500">{s.reason}</div>
                              </td>
                              <td className="px-3 py-2 text-right font-mono">
                                {s.min_cover_days !== null && s.min_cover_days !== undefined ? `${s.min_cover_days}d` : "—"}
                              </td>
                              <td className="px-3 py-2">
                                <Badge className={s.band === "critical"
                                    ? "bg-red-50 text-brand-red border border-red-200"
                                    : "bg-amber-50 text-amber-800 border border-amber-200"}>
                                  {s.band.toUpperCase()}
                                </Badge>
                              </td>
                              <td className="px-3 py-2">
                                <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase text-brand-red">
                                  <ShieldAlert className="h-3 w-3" /> Action needed
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </Card>

              {/* Sales trend — bar + line + zone/store toggle */}
              <Card className="p-5 rounded-xl border-gray-200 bg-white">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Sales trend</div>
                    <div className="font-display text-xl font-bold text-gray-900 mt-0.5">Last {days} days</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Tabs value={trendView} onValueChange={setTrendView}>
                      <TabsList className="bg-gray-100 rounded-lg p-1" data-testid="client-trend-scope-toggle">
                        <TabsTrigger value="store" data-testid="scope-store" className="data-[state=active]:bg-white data-[state=active]:text-brand-red rounded-md text-xs px-3">
                          <Layers className="h-3 w-3 mr-1" /> Store-wise
                        </TabsTrigger>
                        <TabsTrigger value="zone" data-testid="scope-zone" className="data-[state=active]:bg-white data-[state=active]:text-brand-red rounded-md text-xs px-3">
                          <Layers className="h-3 w-3 mr-1" /> Zone-wise
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                    <Tabs value={period} onValueChange={setPeriod}>
                      <TabsList className="bg-gray-100 rounded-lg p-1" data-testid="client-trend-toggle">
                        <TabsTrigger value="weekly" data-testid="trend-weekly" className="data-[state=active]:bg-white data-[state=active]:text-brand-red rounded-md text-xs px-3">Weekly</TabsTrigger>
                        <TabsTrigger value="monthly" data-testid="trend-monthly" className="data-[state=active]:bg-white data-[state=active]:text-brand-red rounded-md text-xs px-3">Monthly</TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-1">
                      {trendView === "zone" ? "Cups by zone" : "Cups by store (top 15)"}
                    </div>
                    <div className="h-64" data-testid="client-trend-bar">
                      <ResponsiveContainer>
                        <BarChart data={barData} margin={{ top: 5, right: 5, left: 0, bottom: 30 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" interval={0} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                          <Bar dataKey="cups" fill={RED} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-1">
                      {trendView === "zone" ? "Daily cups by zone" : "Daily cups total"}
                    </div>
                    <div className="h-64" data-testid="client-trend-line">
                      <ResponsiveContainer>
                        <LineChart data={lineData.rows} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                          {trendView === "zone" && <Legend wrapperStyle={{ fontSize: 11 }} />}
                          {lineData.series.map((k, i) => {
                            const palette = [RED, DARK, "#F59E0B", "#0891B2", "#7C3AED", "#16A34A", "#DB2777"];
                            return (
                              <Line key={k} type="monotone" dataKey={k}
                                    stroke={palette[i % palette.length]}
                                    strokeWidth={2}
                                    dot={{ r: 2 }} activeDot={{ r: 4 }} />
                            );
                          })}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <Card className="p-5 rounded-xl border-gray-200 bg-white" data-testid="client-top-stores">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="h-4 w-4 text-green-600" />
                    <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Top performing stores (5)</div>
                  </div>
                  <div className="space-y-2">
                    {(trend?.top || []).map((s, i) => (
                      <div key={s.store_id} className="flex items-center justify-between p-2 border border-gray-100 rounded-lg" data-testid={`top-store-${i}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="h-7 w-7 rounded-lg bg-green-50 text-green-700 flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</div>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{s.store_name}</div>
                            <div className="text-[10px] text-gray-500">Avg {formatNum(s.avg_daily_cups)} cups/day</div>
                          </div>
                        </div>
                        <div className="text-right shrink-0 pl-2">
                          <div className="text-sm font-bold">{formatNum(s.cups)}</div>
                          <div className="text-[10px] text-green-700">cups</div>
                        </div>
                      </div>
                    ))}
                    {(!trend || trend.top.length === 0) && <div className="text-center text-sm text-gray-400 py-6">No data yet</div>}
                  </div>
                </Card>

                <Card className="p-5 rounded-xl border-gray-200 bg-white" data-testid="client-low-stores">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingDown className="h-4 w-4 text-brand-red" />
                    <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Low performing stores</div>
                  </div>
                  <div className="space-y-2">
                    {(trend?.low || []).map((s, i) => (
                      <div key={s.store_id} className="flex items-center justify-between p-2 border border-gray-100 rounded-lg" data-testid={`low-store-${i}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="h-7 w-7 rounded-lg bg-red-50 text-brand-red flex items-center justify-center text-xs font-bold shrink-0">!</div>
                          <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{s.store_name}</div>
                            <div className="text-[10px] text-gray-500">Avg {formatNum(s.avg_daily_cups)} cups/day</div>
                          </div>
                        </div>
                        <div className="text-right shrink-0 pl-2">
                          <div className="text-sm font-bold">{formatNum(s.cups)}</div>
                          <div className="text-[10px] text-brand-red">cups</div>
                        </div>
                      </div>
                    ))}
                    {(!trend || trend.low.length === 0) && <div className="text-center text-sm text-gray-400 py-6">No data yet</div>}
                  </div>
                </Card>
              </div>

              {/* Material Consumption — dashboard slim: 3 tiles only. Period is fixed = 1st of current month → today. */}
              <Card className="p-5 rounded-xl border-gray-200 bg-white" data-testid="client-consumption-card">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Material consumption</div>
                    <div className="text-[11px] text-gray-500 mt-0.5" data-testid="cons-period-label">
                      {pre.thisMonth.from} to {pre.thisMonth.to} · this month
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  {[
                    { code: "BIB",       label: "BIB consumed",       unit: "units", color: "text-brand-red", icon: "M4 8h16l-1 11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 8Zm2-3h12l-1 3H7l-1-3Z" },
                    { code: "CO2",       label: "CO2 consumed",       unit: "units", color: "text-blue-700", icon: "M12 2a3 3 0 0 0-3 3v2H8a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-1V5a3 3 0 0 0-3-3Zm-1 3a1 1 0 1 1 2 0v2h-2V5Z" },
                    { code: "Water Jar", label: "Water Jar consumed", unit: "jars",  color: "text-cyan-700", icon: "M9 2h6l-1 3h1a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h1L9 2Z" },
                  ].map(({ code, label, unit, color, icon }) => (
                    <div key={code} className="rounded-lg border border-gray-200 p-3 flex items-start gap-3"
                         data-testid={`client-consumption-${code.replace(/\s+/g, "-").toLowerCase()}`}>
                      <div className={`h-10 w-10 rounded-md bg-gray-50 flex items-center justify-center ${color}`}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d={icon} /></svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">{label}</div>
                        <div className={`font-display text-2xl font-black mt-1 ${color}`}>
                          {consumptionMTD ? formatNum(consumptionMTD.totals?.[code] || 0) : "—"} <span className="text-sm font-semibold text-gray-500">{unit}</span>
                        </div>
                        <div className="text-[11px] text-gray-500 mt-1">This month</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Live store list — Coca-Cola dashboard. Attendance-based blinking status; NO revenue/target/stock-days columns. */}
              <Card className="p-5 rounded-xl border-gray-200 bg-white overflow-hidden">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Live store performance</div>
                  <div className="text-[11px] text-gray-500" data-testid="live-summary-strip">
                    <span className="text-green-700 font-semibold">{dailyRows.filter(r => r.attendance_today && !r.has_open_issue).length}</span> live ·{" "}
                    <span className="text-brand-red font-semibold">{dailyRows.filter(r => r.has_open_issue).length}</span> issue{dailyRows.filter(r => r.has_open_issue).length === 1 ? "" : "s"}{" "}
                    of {dailyRows.length}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="client-live-store-table">
                    <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                      <tr>
                        <th className="text-left px-3 py-2">Status</th>
                        <th className="text-left px-3 py-2">Store</th>
                        <th className="text-left px-3 py-2">City</th>
                        <th className="text-right px-3 py-2">Total Cups Today</th>
                        <th className="text-right px-3 py-2">Total Cups Sold</th>
                        <th className="text-right px-3 py-2">Active Days</th>
                        <th className="text-right px-3 py-2">Average Sales</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...dailyRows]
                        .sort((a, b) => (b.glasses_sold - a.glasses_sold) || ((b.total_cups_sold_all_time || 0) - (a.total_cups_sold_all_time || 0)))
                        .slice(0, 30).map((r) => {
                          const status = r.has_open_issue ? "issue" : r.attendance_today ? "live" : "none";
                          return (
                            <tr key={r.store_id} className="border-t border-gray-100" data-testid={`client-live-row-${r.store_code}`}>
                              <td className="px-3 py-2">
                                {status === "issue" && (
                                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-brand-red" data-testid={`status-${r.store_code}`}>
                                    <span className="live-dot inline-block h-2.5 w-2.5 rounded-full bg-red-500 text-red-500" />
                                    Issue
                                  </span>
                                )}
                                {status === "live" && (
                                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-green-700" data-testid={`status-${r.store_code}`}>
                                    <span className="live-dot inline-block h-2.5 w-2.5 rounded-full bg-green-500 text-green-500" />
                                    Live
                                  </span>
                                )}
                                {status === "none" && (
                                  <span className="text-[11px] text-gray-400" data-testid={`status-${r.store_code}`}>—</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <div className="font-semibold">{r.store_name}</div>
                                <div className="text-[10px] text-gray-500">{r.store_code}</div>
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-600">{r.city}</td>
                              <td className="px-3 py-2 text-right font-semibold">{formatNum(r.glasses_sold)}</td>
                              <td className="px-3 py-2 text-right font-mono">{formatNum(r.total_cups_sold_all_time || 0)}</td>
                              <td className="px-3 py-2 text-right font-mono">{r.active_days || 0}</td>
                              <td className="px-3 py-2 text-right font-mono" data-testid={`avg-sales-${r.store_code}`}>
                                {r.active_days > 0 ? formatNum(r.avg_sales || 0) : "—"}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
                <div className="mt-2 text-[11px] text-gray-500 flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-green-500" /> Live = attendance marked today</span>
                  <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-red-500" /> Issue = open issue on store (red takes priority)</span>
                  <span>— = no attendance today</span>
                </div>
              </Card>

              <ClientNotesFeed />

              {/* Footer strip removed per COO request — page ends cleanly here. */}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
